import { apm } from "@elastic/apm-rum";
import {
  Delete,
  KeyboardBackspaceOutlined,
  SaveAsOutlined,
} from "@mui/icons-material";
import { Box, Button, Typography } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

import { monitor } from "shared/common/services/apm-logging";
import { defaultStore } from "shared/common/stores/defaultStore";
import { sidebarStore } from "shared/common/stores/sidebarStore";
import getChangedFeaturesList from "shared/common/utils/layers/getChangedFeaturesList";

export default function SaveChanges() {
  const { t } = useTranslation();

  const changeAlertSaveLayer = defaultStore(
    (state) => state.changeAlertSaveLayer
  );
  const changeAlertMessage = defaultStore((state) => state.changeAlertMessage);
  const viewer = defaultStore((state) => state.viewer);

  const changeUpdateFeaturesList = sidebarStore(
    (state) => state.changeUpdateFeaturesList
  );
  const changeFeatureTableAddedRows = sidebarStore(
    (state) => state.changeFeatureTableAddedRows
  );

  // discard changes from layer
  const discardChanges = () => {
    const activeLayer = viewer?.getScene().getLayerHandler().getActiveLayer();
    if (activeLayer) {
      activeLayer.reset();
      activeLayer.reload();

      //this state update features list table
      changeUpdateFeaturesList(true);

      changeFeatureTableAddedRows(getChangedFeaturesList(activeLayer));
    }
    changeAlertSaveLayer(false);
  };

  // save changes on layer
  const saveChanges = () => {
    const activeLayer = viewer?.getScene().getLayerHandler().getActiveLayer();
    if (activeLayer) {
      activeLayer.save(
        () => {
          activeLayer.setEditMode(true);
          changeFeatureTableAddedRows(getChangedFeaturesList(activeLayer));
          changeAlertMessage({
            message: t("edit_feature.save_message"),
            type: "success",
          });
          return {}; // Due to a TypeScript Bug in the SDK
        },
        (error) => {
          console.error("Error on save:", error);
          changeAlertMessage({
            message: t("edit_feature.error_on_save_message"),
            type: "error",
          });
          return {}; // Due to a TypeScript Bug in the SDK
        }
      );
      changeFeatureTableAddedRows(getChangedFeaturesList(activeLayer));
    }
    activeLayer?.featureselector.clearSelection();
    changeUpdateFeaturesList(true);
    changeAlertSaveLayer(false);
  };

  // close saveChanges alert doing nothing
  const cancel = () => {
    changeAlertSaveLayer(false);
  };

  return (
    <Box
      sx={{
        position: "absolute",
        width: "100%",
        height: "100%",
        zIndex: "100",
        paddingTop: "15%",
        display: "flex",
        justifyContent: "center",
        backgroundColor: "blackBackground.main",
      }}
    >
      <Box
        sx={{
          display: "flex",
          height: "fit-content",
          flexDirection: "column",
          paddingY: 2,
          paddingX: 4,
          margin: 2,
          gap: 2,
          backgroundColor: "white",
          borderRadius: "8px",
        }}
      >
        <Typography
          sx={{
            color: "darkblue.main",
          }}
        >
          {t("feature_alert.save_message")}
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
          }}
        >
          <Button
            onClick={() => monitor(apm, "cancel-changes-alert | Edit", cancel)}
            variant="outlined"
            sx={{ color: "darkblue.main", borderColor: "darkblue.main" }}
          >
            <KeyboardBackspaceOutlined fontSize="small" />{" "}
            {t("feature_alert.save_back_button")}
          </Button>
          <Button
            onClick={() =>
              monitor(apm, "discard-changes-alert | Edit", discardChanges)
            }
            variant="outlined"
            sx={{ color: "darkblue.main", borderColor: "darkblue.main" }}
          >
            <Delete fontSize="small" /> {t("feature_alert.save_discard_button")}
          </Button>
          <Button
            onClick={() =>
              monitor(apm, "save-changes-alert | Edit", saveChanges)
            }
            variant="outlined"
            sx={{ color: "darkblue.main", borderColor: "darkblue.main" }}
          >
            <SaveAsOutlined fontSize="small" />{" "}
            {t("feature_alert.save_save_button")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
